import queryString from 'query-string'
import moment from 'moment'
import yn from 'yn'
import dateFormats from '../configuration/dateFormats'
import * as UserSelectionActions from '../redux/userSelections/actions'
import {
  setDepartureDate,
  setDepartureRoute,
  setReturnDate,
  setReturnRoute,
  setRouteForm
} from './ferryRouteSelections/actions'
import {
  setDepartureAdults,
  setDepartureChildren,
  setDepartureInfants,
  setDepartureSeniors,
  setDepartureStudents,
  setDeparturePets,
  setDuplicatePassengerQuantities,
  setReturnAdults,
  setReturnChildren,
  setReturnInfants,
  setReturnSeniors,
  setReturnStudents,
  setReturnPets
} from './passengerSelections/actions'
import {
  resetVehicleSelections,
  setVehicleFormType
} from './vehicleSelections/actions'
import { vehicleTypes } from './vehicleSelections/constants'
import {
  appRoutes,
  ENABLE_ACCOMMODATIONS,
  noDefaultRoutes
} from '../configuration/constants'
import {
  resetActiveCrossings,
  resetReturnCrossing
} from './activeCrossings/actions'
import { resetActiveAccommodations } from './activeAccommodations/actions'
import { clearBookingData } from './booking/actions'
import { clearPaymentConfirmation } from './paymentConfirmation/actions'
import filterGroupRoutes from '../utilities/content/filterGroupRoutes'
import { isValidTripType } from './vehicleSelections/utilities'
import { fetchPromoInformation } from './promo/actions'
import { pushToDataLayer } from '../utilities/analytics'

export const ingestQueryParameters = (
  queryParams = queryString.parse(window.location.search),
  configOptions
) => async dispatch => {
  const {
    adults,
    children,
    departureAdults,
    departureChildren,
    departureDate,
    ddate,
    departureInfants,
    departureRoute,
    departureSeniors,
    departureStudents,
    // PETS
    pets,
    departurePets,
    returnPets,
    ferry,
    infants,
    returnAdults,
    returnChildren,
    returnDate,
    rdate,
    returnInfants,
    returnRoute,
    returnSeniors,
    returnStudents,
    seniors,
    students,
    vehicleType,
    tripType,
    coupon
  } = queryParams

  const { routes: availableRoutes } = configOptions

  if (
    [
      appRoutes.bookingConfirmation.pathname,
      appRoutes.summary.pathname
    ].includes(window.location.pathname)
  ) {
    // We were redirected from Moneris hosted page, don't clear the persisted state
    return
  }

  /* DATES
   *  we'll assume that without an explicit tripType,
   *  the presence of both departure and return dates means we want a return trip.
   * */
  const departureDateParam = departureDate || ddate
  const returnDateParam = returnDate || rdate
  if (
    departureDateParam &&
    moment(departureDateParam, dateFormats.default).isSameOrAfter(
      moment(),
      'days'
    )
  ) {
    dispatch(setDepartureDate(moment(departureDateParam, dateFormats.default))) // TODO: deprecate
    dispatch(UserSelectionActions.setDepartureSearchDate(departureDateParam))
    if (!returnDateParam && !tripType) {
      dispatch(setRouteForm('OW')) // TODO: deprecate
    } else {
      if (
        moment(returnDateParam, dateFormats.default).isSameOrAfter(
          moment(departureDateParam, dateFormats.default),
          'days'
        )
      ) {
        dispatch(setReturnDate(moment(returnDateParam, dateFormats.default))) // TODO: deprecate
        dispatch(UserSelectionActions.setReturnSearchDate(returnDateParam))
      }
      if (!tripType) {
        dispatch(setRouteForm('RT')) // TODO: deprecate
      }
    }
  }
  if (coupon) {
    await dispatch(fetchPromoInformation(coupon, { clearIfInvalid: true }))
  }

  if (tripType && isValidTripType(tripType)) {
    dispatch(setRouteForm(tripType)) // TODO: deprecate
    dispatch(UserSelectionActions.setTripType(tripType))
  }

  /* ROUTES
   * we set the routes based on the query params, otherwise select the first available routes to give the
   * app some initial values.
   * */
  if (availableRoutes) {
    const shouldSetPaymentId =
      window.location.pathname !== appRoutes.telephoneBooking.pathname
    const matchingDepartureRoute = getMatchingDepartureRoute(
      availableRoutes,
      departureRoute,
      ferry,
      noDefaultRoutes
    )

    if (matchingDepartureRoute) {
      dispatch(
        setDepartureRoute(
          matchingDepartureRoute,
          shouldSetPaymentId,
          departureDateParam,
          returnDateParam
        )
      ) // TODO: deprecate
      dispatch(
        UserSelectionActions.setDepartureRoute(matchingDepartureRoute.code)
      )
    }

    const validReturnRoutes =
      yn(process.env.REACT_APP_COMPONENT_ENABLE_ROUTE_GROUPINGS) && ferry
        ? filterGroupRoutes(availableRoutes, ferry)
        : availableRoutes

    const matchingReturnRoute =
      returnRoute && returnRoute !== departureRoute
        ? validReturnRoutes.find(
            route => route.code.toLowerCase() === returnRoute.toLowerCase()
          )
        : matchingDepartureRoute
        ? validReturnRoutes.find(
            route =>
              route.code.toLowerCase() !==
              matchingDepartureRoute.code.toLowerCase()
          )
        : noDefaultRoutes
        ? null
        : validReturnRoutes[1]

    if (matchingReturnRoute) {
      dispatch(setReturnRoute(matchingReturnRoute)) // TODO: deprecate
      dispatch(UserSelectionActions.setReturnRoute(matchingReturnRoute.code))
    }
  }

  /*
   * PASSENGERS
   * we'll assume adults or departureAdults, etc. are the same thing.
   * returnAdults, returnChildren etc, will only apply to return trips
   *
   * */
  const totalAllowedTickets = +process.env
    .REACT_APP_COMPONENT_MAXIMUM_PASSENGER_TICKETS

  let remainingDeparture = totalAllowedTickets
  let remainingReturn = totalAllowedTickets

  const getCountSafely = values => +values.find(x => !!x) || 0
  const setAndUpdateCount = (count, setCountAction, isDeparture) => {
    const amountToSet = Math.min(
      count,
      isDeparture ? remainingDeparture : remainingReturn
    )
    if (isDeparture) {
      remainingDeparture = Math.max(remainingDeparture - amountToSet, 0)
    } else {
      remainingReturn = Math.max(remainingReturn - amountToSet, 0)
    }
    dispatch(setCountAction(amountToSet))
  }

  const adultCount = getCountSafely([adults, departureAdults])
  const childCount = getCountSafely([children, departureChildren])
  const seniorCount = getCountSafely([seniors, departureSeniors])
  const infantCount = getCountSafely([infants, departureInfants])
  const studentCount = getCountSafely([students, departureStudents])
  const petCount = getCountSafely([pets, departurePets])

  setAndUpdateCount(adultCount, setDepartureAdults, true) // TODO: deprecate
  setAndUpdateCount(childCount, setDepartureChildren, true) // TODO: deprecate
  setAndUpdateCount(seniorCount, setDepartureSeniors, true) // TODO: deprecate
  setAndUpdateCount(infantCount, setDepartureInfants, true) // TODO: deprecate
  setAndUpdateCount(studentCount, setDepartureStudents, true) // TODO: deprecate
  setAndUpdateCount(petCount, setDeparturePets, true)

  UserSelectionActions.updateDeparturePassengers({
    adult: adultCount,
    child: childCount,
    senior: seniorCount,
    infant: infantCount,
    student: studentCount,
    pet: petCount
  })

  if (
    returnAdults ||
    returnSeniors ||
    returnChildren ||
    returnInfants ||
    returnStudents ||
    returnPets
  ) {
    // this means at least one of these is set and we need to set our
    // duplicate passenger flag (default is true)
    dispatch(setDuplicatePassengerQuantities(false)) // TODO: deprecate
    dispatch(UserSelectionActions.setIsSamePassengers(false))

    const adultCountReturn = getCountSafely([returnAdults])
    const childCountReturn = getCountSafely([returnChildren])
    const seniorCountReturn = getCountSafely([returnSeniors])
    const infantCountReturn = getCountSafely([returnInfants])
    const studentCountReturn = getCountSafely([returnStudents])
    const petCountReturn = getCountSafely([returnPets])
    setAndUpdateCount(adultCountReturn, setReturnAdults, false) // TODO: deprecate
    setAndUpdateCount(childCountReturn, setReturnChildren, false) // TODO: deprecate
    setAndUpdateCount(seniorCountReturn, setReturnSeniors, false) // TODO: deprecate
    setAndUpdateCount(infantCountReturn, setReturnInfants, false) // TODO: deprecate
    setAndUpdateCount(studentCountReturn, setReturnStudents, false) // TODO: deprecate
    setAndUpdateCount(petCountReturn, setReturnPets, false)

    UserSelectionActions.updateReturnPassengers({
      adult: adultCountReturn,
      child: childCountReturn,
      senior: seniorCountReturn,
      infant: infantCountReturn,
      student: studentCountReturn,
      pet: petCountReturn
    })
  } else {
    setAndUpdateCount(adultCount, setReturnAdults, false) // TODO: deprecate
    setAndUpdateCount(childCount, setReturnChildren, false) // TODO: deprecate
    setAndUpdateCount(seniorCount, setReturnSeniors, false) // TODO: deprecate
    setAndUpdateCount(infantCount, setReturnInfants, false) // TODO: deprecate
    setAndUpdateCount(studentCount, setReturnStudents, false) // TODO: deprecate
    setAndUpdateCount(petCount, setReturnPets, false)

    UserSelectionActions.updateReturnPassengers({
      adult: adultCount,
      child: childCount,
      senior: seniorCount,
      infant: infantCount,
      student: studentCount,
      pet: petCount
    })
  }

  /*
   * Vehicles
   * walk-on, duplicateVehicle, uniqueVehicle
   * */
  if (vehicleType && vehicleTypes.some(type => type === vehicleType)) {
    dispatch(setVehicleFormType(vehicleType)) // TODO: deprecate
  }
}

function getMatchingDepartureRoute(
  availableRoutes,
  departureRoute,
  ferry,
  noDefaultRoutes
) {
  if (departureRoute) {
    return availableRoutes.find(
      route => route.code.toLowerCase() === departureRoute.toLowerCase()
    )
  } else if (ferry) {
    return availableRoutes.find(
      route => route.ferryId.toLowerCase() === ferry.toLowerCase()
    )
  } else if (noDefaultRoutes) {
    return null
  } else {
    return availableRoutes[0]
  }
}

// used to clear selections made before returning to an earlier step.
export const cleanRedux = pathname => dispatch => {
  if (
    pathname === appRoutes.ferryRoutes.pathname ||
    pathname === appRoutes.passengerQuantities.pathname
  ) {
    dispatch(resetVehicleSelections())
    dispatch(resetActiveCrossings())
    if (ENABLE_ACCOMMODATIONS) dispatch(resetActiveAccommodations())
    dispatch(clearBookingData())
    dispatch(clearPaymentConfirmation())
  }
  if (pathname === appRoutes.vehicle.pathname) {
    dispatch(resetActiveCrossings())
    if (ENABLE_ACCOMMODATIONS) dispatch(resetActiveAccommodations())
    dispatch(clearBookingData())
    dispatch(clearPaymentConfirmation())
  }
  if (pathname === appRoutes.departure.pathname) {
    dispatch(resetReturnCrossing())
    if (ENABLE_ACCOMMODATIONS) dispatch(resetActiveAccommodations())
    dispatch(clearBookingData())
    dispatch(clearPaymentConfirmation())
  }
  if (
    pathname === appRoutes.return.pathname ||
    pathname === appRoutes.accommodations.pathname ||
    pathname === appRoutes.extras.pathname
  ) {
    dispatch(clearBookingData())
    dispatch(clearPaymentConfirmation())
  }
  if (
    pathname === appRoutes.bookingConfirmation.pathname ||
    pathname === appRoutes.bookingSuccess.pathname
  ) {
    // clearup previously modified stuff after complete booking workflow successfully,
    // to ensure next modify refilled correctly!
    dispatch(resetVehicleSelections())
    if (ENABLE_ACCOMMODATIONS) dispatch(resetActiveAccommodations())
  }
  if (pathname === appRoutes.account.pathname) {
    dispatch(clearPaymentConfirmation())
  }
}

export const dispatchAnalyticsEvent = pathname => (dispatch, getState) => {
  const state = getState()

  const departureDescription =
    state.ferryRouteSelections?.departureRoute?.activeCrossingData?.description

  const destinationItems = departureDescription
    ?.split('»')?.[1]
    ?.trim()
    ?.split(',')

  const data = {
    event: pathname,
    departureDate:
      state?.activeCrossings?.departure?.departureDate?.split('T')?.[0] ||
      moment(state?.ferryRouteSelections?.departureDate)?.format('YYYY-DD-MM'),
    returnDate: state?.activeCrossings?.return?.departureDate?.split('T')?.[0]
      ? state?.activeCrossings?.return?.departureDate?.split('T')?.[0]
      : state?.ferryRouteSelections?.returnDate
      ? moment(state?.ferryRouteSelections?.returnDate)?.format('YYYY-DD-MM')
      : '',
    city: destinationItems?.[0] /* Destination City */,
    state: destinationItems?.[1]?.trim(),
    country: destinationItems?.[1]?.trim() === 'ME' ? 'USA' : 'CAN',
    attractionName:
      state?.ferryRouteSelections?.departureRoute
        ?.ferryId /* Send the ferry that was booked ex: "CAT" */
  }

  pushToDataLayer(data)
}
